import { useFormikContext } from "formik";
import { useEffect, useMemo } from "react";

import HideAnimation from "web/Components/Common/HideAnimation";

import { useGetCompanyDetailsQuery } from "web/features/company/companyApiSlice";
import { useGetEmployeeConfigurationQuery } from "web/features/employee/employeeApiSlice";

import RentableGroupCalculator from "./RentableGroupCalculator";
import RentableGroupCalculatorInstruction from "./RentableGroupCalculatorInstruction";
import RentableGroupCalculatorResult from "./RentableGroupCalculatorResult";
import RentableGroupCalculatorSelectedGroup from "./RentableGroupCalculatorSelectedGroup";
import RentableGroupCalculatorStatements from "./RentableGroupCalculatorStatements";
import RentableGroupCalculatorTotalIncomeMessage from "./RentableGroupCalculatorTotalIncomeMessage";
import shouldShowCalculator from "./utils/shouldShowCalculator";
import useCalculateRentableGroup from "./utils/useCalculateRentableGroup";
import useRentableGroupCalculatorStatements from "./utils/useRentableGroupsCalculatorStatements";
import getRentableGroupCalculatorCopy from "./utils/getRentableGroupCalculatorCopy";

export interface IRentableGroupCalculatorValues {
  income: {
    me: number;
    people: { role: string; income: number }[];
  };
  "fill-calculator-checkbox"?: boolean;
  "financing-checkbox"?: boolean;
  incomeGroupId?: string;
  agreements?: Record<string, boolean>;
}

const MAX_TOTAL_INCOME = 999999999.99;

export type Statements = ("fill-calculator-checkbox" | "financing-checkbox")[];

const RentableGroupCalculatorContainer = () => {
  const { values, setFieldValue } =
    useFormikContext<IRentableGroupCalculatorValues>();
  const { data: employeeConfig } = useGetEmployeeConfigurationQuery();
  const { data: companyDetails } = useGetCompanyDetailsQuery();

  const statements = useRentableGroupCalculatorStatements();

  const { peopleCount, averageIncome, incomeGroup, totalIncome } =
    useCalculateRentableGroup(values);

  const calculatorCopy = useMemo(
    () =>
      getRentableGroupCalculatorCopy(
        employeeConfig,
        companyDetails?.id as string
      ),
    [employeeConfig, companyDetails?.id]
  );

  const {
    instructionHeader,
    peopleCount: peopleCountCopy,
    averageIncome: averageIncomeCopy,
    incomeGroup: incomeGroupCopy,
    incomeGroupDeclared: incomeGroupDeclaredCopy,
    noIncomeGroupAvailable,
  } = calculatorCopy;

  useEffect(() => {
    setFieldValue("incomeGroupId", incomeGroup?.id);
  }, [incomeGroup?.id]);

  const showCalculator = useMemo(
    () => shouldShowCalculator(values, statements),
    [values, statements]
  );

  const showSelectedGroup = useMemo(
    () =>
      incomeGroup === null ||
      shouldShowCalculator(values, ["financing-checkbox"]),
    [values, incomeGroup]
  );

  const incomeGroupCopyDisplayed = useMemo(() => {
    const incomeGroupCopyToShow = showCalculator
      ? incomeGroupCopy
      : incomeGroupDeclaredCopy;
    return incomeGroup?.id ? incomeGroupCopyToShow : noIncomeGroupAvailable;
  }, [showCalculator, incomeGroup?.id]);

  const isTotalIncomeTooHigh = useMemo(
    () => !!totalIncome && totalIncome > MAX_TOTAL_INCOME,
    [totalIncome]
  );

  useEffect(() => {
    if (isTotalIncomeTooHigh) {
      setFieldValue("isTotalIncomeTooHigh", true);
    } else {
      setFieldValue("isTotalIncomeTooHigh", false);
    }
  }, [isTotalIncomeTooHigh]);

  return (
    <>
      <HideAnimation isOpen={showCalculator}>
        <RentableGroupCalculatorInstruction paragraph={instructionHeader} />
      </HideAnimation>

      <RentableGroupCalculatorStatements statements={statements} calculatorCopy={calculatorCopy} />

      <HideAnimation isOpen={showCalculator}>
        <RentableGroupCalculator calculatorCopy={calculatorCopy} />
        <RentableGroupCalculatorResult
          peopleCount={peopleCount}
          peopleCountCopy={peopleCountCopy}
          averageIncome={averageIncome}
          averageIncomeCopy={averageIncomeCopy}
        />
      </HideAnimation>
      {isTotalIncomeTooHigh && (
        <RentableGroupCalculatorTotalIncomeMessage totalIncome={totalIncome!} calculatorCopy={calculatorCopy} />
      )}
      <HideAnimation isOpen={showSelectedGroup}>
        <RentableGroupCalculatorSelectedGroup
          incomeGroup={incomeGroup?.frontendName}
          incomeGroupCopy={incomeGroupCopyDisplayed}
        />
      </HideAnimation>
    </>
  );
};
export default RentableGroupCalculatorContainer;
